/**
 * SpotlightSearch — iOS-style universal search overlay.
 *
 * Pulled down from the home screen, shows a full-screen search
 * that filters across all registered apps (installed + catalog).
 * Features:
 *  - Animated slide-down entrance
 *  - Auto-focused search input
 *  - Real-time fuzzy filtering by name, tags, description
 *  - Grouped results: Installed apps, then available apps
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useHomeScreenStore, useAppRegistryStore, MediaIcon } from '@room/shared-ui';
import './SpotlightSearch.css';

interface SpotlightSearchProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenApp: (route: string) => void;
}

interface SearchResult {
  id: string;
  label: string;
  icon: string;
  route: string;
  installed: boolean;
  matchType: 'name' | 'tag' | 'description';
}

function isImageIcon(icon: string): boolean {
  return (
    icon.startsWith('http://') ||
    icon.startsWith('https://') ||
    icon.startsWith('data:image/') ||
    icon.startsWith('/api/')
  );
}

function ResultIcon({ icon, id }: { icon: string; id: string }) {
  if (isImageIcon(icon)) {
    return <img src={icon} alt="" className="spotlight__result-img" draggable={false} />;
  }
  return (
    <MediaIcon
      mediaId={id}
      fallback={icon}
      className="spotlight__result-media-icon"
    />
  );
}

export function SpotlightSearch({ isOpen, onClose, onOpenApp }: SpotlightSearchProps) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const items = useHomeScreenStore((s) => s.items);
  const manifests = useAppRegistryStore((s) => s.manifests);
  const installedApps = useAppRegistryStore((s) => s.installedApps);

  // Auto-focus input when opened
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      // Small delay to wait for animation
      const timer = setTimeout(() => inputRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  const results = useMemo((): SearchResult[] => {
    const q = query.toLowerCase().trim();

    // Build combined list from manifests + registered tiles
    const appMap = new Map<string, SearchResult>();

    // Add all manifests
    Object.values(manifests).forEach((m) => {
      if (m.id === 'home' || m.id === 'login') return;
      appMap.set(m.id, {
        id: m.id,
        label: m.tile?.label || m.name || m.id,
        icon: m.icon || '📱',
        route: m.tile?.route || `/${m.id}`,
        installed: installedApps.includes(m.id) || m.category === 'core',
        matchType: 'name',
      });
    });

    // Add registered tiles not in manifests
    items.forEach((tile, id) => {
      if (appMap.has(id) || id === 'home' || id === 'login') return;
      appMap.set(id, {
        id,
        label: tile.label,
        icon: tile.icon,
        route: tile.route,
        installed: true,
        matchType: 'name',
      });
    });

    const all = Array.from(appMap.values());
    if (!q) return all.filter((a) => a.installed);

    // Filter and score
    return all
      .map((app) => {
        const name = app.label.toLowerCase();
        if (name.includes(q)) return { ...app, matchType: 'name' as const };

        const manifest = manifests[app.id];
        if (manifest?.tags?.some((t: string) => t.toLowerCase().includes(q))) {
          return { ...app, matchType: 'tag' as const };
        }
        if (manifest?.description?.toLowerCase().includes(q)) {
          return { ...app, matchType: 'description' as const };
        }
        return null;
      })
      .filter(Boolean) as SearchResult[];
  }, [query, manifests, items, installedApps]);

  const installedResults = results.filter((r) => r.installed);
  const availableResults = results.filter((r) => !r.installed);

  const handleSelect = useCallback(
    (route: string) => {
      onClose();
      onOpenApp(route);
    },
    [onClose, onOpenApp],
  );

  if (!isOpen) return null;

  return (
    <div className="spotlight" onClick={onClose}>
      <div className="spotlight__container" onClick={(e) => e.stopPropagation()}>
        {/* Search bar */}
        <div className="spotlight__search-bar">
          <svg className="spotlight__search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            className="spotlight__input"
            placeholder="Search apps..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
          {query && (
            <button className="spotlight__clear" onClick={() => setQuery('')} aria-label="Clear search">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7" fill="rgba(142,142,147,0.5)" />
                <path d="M5.5 5.5L10.5 10.5M10.5 5.5L5.5 10.5" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </div>

        {/* Results */}
        <div className="spotlight__results">
          {installedResults.length > 0 && (
            <div className="spotlight__section">
              {query && <div className="spotlight__section-title">Installed</div>}
              <div className="spotlight__grid">
                {installedResults.map((app) => (
                  <button
                    key={app.id}
                    className="spotlight__result"
                    onClick={() => handleSelect(app.route)}
                  >
                    <div className="spotlight__result-icon">
                      <ResultIcon icon={app.icon} id={app.id} />
                    </div>
                    <div className="spotlight__result-label">{app.label}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {availableResults.length > 0 && (
            <div className="spotlight__section">
              <div className="spotlight__section-title">Available in Store</div>
              <div className="spotlight__grid">
                {availableResults.map((app) => (
                  <button
                    key={app.id}
                    className="spotlight__result spotlight__result--dimmed"
                    onClick={() => handleSelect('/store')}
                  >
                    <div className="spotlight__result-icon">
                      <ResultIcon icon={app.icon} id={app.id} />
                    </div>
                    <div className="spotlight__result-label">{app.label}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {results.length === 0 && query && (
            <div className="spotlight__empty">
              No apps found for "{query}"
            </div>
          )}
        </div>

        {/* Cancel button */}
        <button className="spotlight__cancel" onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
}
